/* ==========================================================================
   SMOON — landing MOVE : comportements partagés (v2, 15/09/2026).
   --------------------------------------------------------------------------
   Chargé en <script type="module"> par chacune des sections : le navigateur
   n'exécute un module qu'une fois par URL. Dans l'éditeur de thème,
   `shopify:section:load` relance l'initialisation sur la section réinjectée.

   1. Révélation au scroll des éléments `.sm-anim-*` (primitives SMOON).
   2. Technologie : vidéo collée, textes au fil, une matière lue à la fois.
   3. Module technique Perf : filets tracés du repère vers le produit.
   4. Widget Trustpilot : initialisation explicite, comme sur la home.
   5. Cartes produit : galerie (points, flèches), sélecteur de taille, ajout au
      panier en AJAX sur le contrat de product-form.js, tiroir UpCart.
   6. Visionneuse : clic sur une image de carte, elle s'affiche en grand.
   7. Guide des tailles : bouton des cartes, modale des fiches produit.
   8. Composeur : sélection de plusieurs culottes, remise, ajout groupé.
   9. Offre de lancement : pastilles et liens masqués hors des dates ; même
      mécanisme pour les rappels du cadeau MOVE (produit offert).

   Tout mouvement est neutralisé si l'utilisatrice demande moins d'animation.
   ========================================================================== */

const html = document.documentElement;
html.classList.add('mv-js');

const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
const hasIO = 'IntersectionObserver' in window;

/* 1. Révélation ---------------------------------------------------------- */

function reveal(root) {
  const els = root.querySelectorAll('.mv [class*="sm-anim-"]:not(.sm-in-view)');
  if (!els.length) return;
  if (!hasIO || reduced.matches) {
    els.forEach((el) => el.classList.add('sm-in-view'));
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('sm-in-view');
        io.unobserve(entry.target);
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -5% 0px' }
  );
  els.forEach((el) => io.observe(el));
}

/* 2. Technologie : vidéo collée, textes au fil ---------------------------- */

/* Connexion trop juste pour une vidéo : économiseur de données ou 2G. */
function slowNetwork() {
  const c = navigator.connection;
  return !!(c && (c.saveData || /(^|[^3-5])2g/.test(c.effectiveType || '')));
}

function techno(root) {
  root.querySelectorAll('[data-mv-scroll]').forEach((wrap) => {
    if (wrap.dataset.mvReady) return;
    wrap.dataset.mvReady = '1';
    const steps = Array.from(wrap.querySelectorAll('[data-mv-step]'));
    const clips = Array.from(wrap.querySelectorAll('[data-mv-clip]'));
    const tabs = Array.from(wrap.querySelectorAll('[data-mv-tab]'));
    const stage = wrap.querySelector('[data-mv-stage]');
    if (!steps.length || !stage) return;
    const wantVideo = wrap.dataset.mvVideo === '1' && !reduced.matches && !slowNetwork();
    let current = 0;
    let armed = !hasIO;
    let near = true;

    /* Repli image : la vidéo est retirée de l'écran, l'image d'attente reste. */
    const still = (clip, video) => {
      clip.classList.remove('is-playing');
      clip.classList.add('is-still');
      try { video.pause(); } catch (e) { /* rien à faire */ }
    };

    /* Source posée au premier besoin : la plus petite définition qui couvre le
       cadre (densité plafonnée à 2x), parmi les MP4 transcodés par Shopify.
       Une lecture qui cale six secondes ou refuse de démarrer rend la main à
       l'image. */
    const load = (clip, video) => {
      if (video.dataset.mvLoaded) return true;
      let list = [];
      try { list = JSON.parse(video.dataset.mvSources || '[]'); } catch (e) { list = []; }
      if (!list.length) return false;
      const need = clip.clientHeight * Math.min(window.devicePixelRatio || 1, 2);
      list.sort((a, b) => a.h - b.h);
      const pick = list.find((x) => x.h >= need) || list[list.length - 1];
      video.dataset.mvLoaded = '1';
      let timer = 0;
      const stalled = () => {
        clearTimeout(timer);
        timer = setTimeout(() => still(clip, video), 6000);
      };
      video.addEventListener('waiting', stalled);
      video.addEventListener('stalled', stalled);
      video.addEventListener('pause', () => clearTimeout(timer));
      video.addEventListener('playing', () => {
        clearTimeout(timer);
        clip.classList.add('is-playing');
      });
      video.addEventListener('error', () => {
        clearTimeout(timer);
        still(clip, video);
      });
      video.muted = true;
      video.src = pick.u;
      video.load();
      return true;
    };

    /* Une seule vidéo en lecture : celle de la matière en cours, si la section
       est proche et l'onglet visible. Les autres sont en pause. */
    const play = () => {
      clips.forEach((clip, i) => {
        const video = clip.querySelector('video');
        if (!video || clip.classList.contains('is-still')) return;
        const go = i === current && wantVideo && armed && near && !document.hidden;
        if (go) {
          if (!load(clip, video)) return;
          const p = video.play();
          /* Un pause() pendant qu'un play() est en attente rejette la promesse
             (AbortError) : ce n'est pas un refus de lecture, on ne replie pas. */
          if (p && p.catch) p.catch((err) => { if (!err || err.name !== 'AbortError') still(clip, video); });
        } else if (!video.paused) {
          video.pause();
        }
      });
    };

    const activate = (i) => {
      current = i;
      steps.forEach((el, j) => el.classList.toggle('is-on', j === i));
      clips.forEach((el, j) => el.classList.toggle('is-on', j === i));
      tabs.forEach((el, j) => {
        el.classList.toggle('is-on', j === i);
        if (j === i) el.setAttribute('aria-current', 'true');
        else el.removeAttribute('aria-current');
      });
      play();
    };

    /* Ligne de lecture : desktop, au milieu de l'écran ; mobile, à 40 % de la
       zone qui reste sous la vidéo collée. La matière en cours est la dernière
       dont le texte a franchi cette ligne : le calcul vaut pour toute position,
       saut d'ancre compris (une bande d'IntersectionObserver ratait les textes
       sautés d'un coup). Trois lectures de rectangle par image, seulement quand
       la section est proche. */
    let line = 0;
    const measure = () => {
      const vh = window.innerHeight;
      if (window.matchMedia('(min-width: 990px)').matches) {
        line = vh * 0.5;
      } else {
        const stuck = stage.offsetHeight + (parseFloat(getComputedStyle(stage).top) || 0);
        line = Math.min(vh - 60, stuck + (vh - stuck) * 0.4);
      }
    };
    let raf = 0;
    const update = () => {
      raf = 0;
      let idx = 0;
      steps.forEach((el, i) => {
        if (el.getBoundingClientRect().top <= line) idx = i;
      });
      if (idx !== current || !steps[idx].classList.contains('is-on')) activate(idx);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    const onResize = () => {
      measure();
      onScroll();
    };
    let listening = false;
    const listen = (on) => {
      if (on === listening) return;
      listening = on;
      const m = on ? 'addEventListener' : 'removeEventListener';
      window[m]('scroll', onScroll, { passive: true });
      window[m]('resize', onResize);
      if (on) onResize();
    };

    if (!hasIO) {
      listen(true);
      play();
      return;
    }

    /* Armement à l'approche (aucun octet de vidéo avant) ; à distance, plus
       d'écoute du défilement et vidéo en pause, comme quand l'onglet passe en
       arrière-plan. */
    const nearIO = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          near = entry.isIntersecting;
          if (near) armed = true;
          listen(near);
          play();
        });
      },
      { rootMargin: '60% 0px 60% 0px' }
    );
    nearIO.observe(wrap);
    document.addEventListener('visibilitychange', play);

    /* Éditeur : sélectionner un bloc « Matière » l'affiche. */
    document.addEventListener('shopify:block:select', (e) => {
      const step = e.target && e.target.closest ? e.target.closest('[data-mv-step]') : null;
      const i = steps.indexOf(step);
      if (i >= 0) activate(i);
    });
  });
}

/* 3. Filets du module technique ------------------------------------------ */

function techLines(root) {
  root.querySelectorAll('[data-mv-tech]').forEach((mod) => {
    if (mod.dataset.mvReady) return;
    mod.dataset.mvReady = '1';

    const svg = mod.querySelector('.mv-tech__lines');
    const product = mod.querySelector('[data-mv-product]');
    if (!svg || !product) return;

    const draw = () => {
      const visible = getComputedStyle(svg).display !== 'none';
      /* Dans chaque colonne, les légendes se rangent de haut en bas selon leur
         point d'ancrage vertical : la légende du haut vise le point le plus haut,
         les filets ne se croisent pas, quel que soit l'ordre des blocs. Sur
         mobile (sans filets), l'ordre des blocs est rétabli. */
      mod.querySelectorAll('.mv-tech__col').forEach((col) => {
        const reps = Array.from(col.querySelectorAll('[data-mv-repere]'));
        const tries = reps.slice().sort((a, b) => parseFloat(a.dataset.y || '50') - parseFloat(b.dataset.y || '50'));
        reps.forEach((rep) => { rep.style.order = visible ? String(tries.indexOf(rep)) : ''; });
      });
      if (!visible) return;
      const box = mod.getBoundingClientRect();
      if (!box.width) return;
      const pb = product.getBoundingClientRect();
      svg.setAttribute('viewBox', `0 0 ${box.width} ${box.height}`);
      svg.setAttribute('width', box.width);
      svg.setAttribute('height', box.height);

      let out = '';
      mod.querySelectorAll('[data-mv-repere]').forEach((rep) => {
        const rb = rep.getBoundingClientRect();
        const right = rep.dataset.side === 'right';
        const x1 = (right ? rb.left : rb.right) - box.left;
        const y1 = rb.bottom - box.top;
        const px = parseFloat(rep.dataset.x || '50') / 100;
        const py = parseFloat(rep.dataset.y || '50') / 100;
        const x2 = pb.left - box.left + pb.width * px;
        const y2 = pb.top - box.top + pb.height * py;
        out += `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}"/><circle cx="${x2}" cy="${y2}" r="4"/>`;
      });
      svg.innerHTML = out;
    };

    draw();
    if ('ResizeObserver' in window) {
      new ResizeObserver(draw).observe(mod);
    } else {
      window.addEventListener('resize', draw);
    }
    product.querySelectorAll('img').forEach((img) => img.addEventListener('load', draw));
    mod.addEventListener('transitionend', draw);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(draw);
  });
}

/* 4. Trustpilot ---------------------------------------------------------- */

function trustpilot(root) {
  const widgets = root.querySelectorAll('.mv-tp .trustpilot-widget');
  if (!widgets.length) return;

  if (!document.querySelector('script[src*="tp.widget.bootstrap"]')) {
    const s = document.createElement('script');
    s.async = true;
    s.src = 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
    document.head.appendChild(s);
  }

  widgets.forEach((el) => {
    let n = 0;
    const t = setInterval(() => {
      if (window.Trustpilot && window.Trustpilot.loadFromElement && el.offsetWidth > 0) {
        window.Trustpilot.loadFromElement(el, true);
        clearInterval(t);
      } else if (++n > 60) {
        clearInterval(t);
      }
    }, 150);
  });
}

/* 5. Cartes produit ------------------------------------------------------ */

/* Prix formaté comme Liquid : le gabarit vient de `shop.money_format`. La
   boutique utilise `{{amount_with_comma_separator}} €` ; les huit variables
   Shopify sont gérées pour ne jamais afficher le gabarit brut. */
function money(cents, format) {
  const value = Number(cents || 0) / 100;
  const group = (decimals, thousands, decimal) => {
    const parts = value.toFixed(decimals).split('.');
    const int = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts[1] ? int + decimal + parts[1] : int;
  };
  /* Sans centimes, on écrit « 35 € » comme le fait money_without_trailing_zeros. */
  const entier = Number.isInteger(value);
  const fmt = format || (window.Shopify && window.Shopify.money_format);
  if (!fmt || fmt.indexOf('{{') === -1) return (entier ? group(0, ' ', ',') : group(2, ' ', ',')) + ' €';
  return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name) => {
    switch (name) {
      case 'amount': return entier ? group(0, ',', '.') : group(2, ',', '.');
      case 'amount_no_decimals': return group(0, ',', '.');
      case 'amount_with_comma_separator': return entier ? group(0, '.', ',') : group(2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return group(0, '.', ',');
      case 'amount_with_apostrophe_separator': return entier ? group(0, "'", '.') : group(2, "'", '.');
      case 'amount_no_decimals_with_space_separator': return group(0, ' ', ',');
      case 'amount_with_space_separator': return entier ? group(0, ' ', ',') : group(2, ' ', ',');
      case 'amount_with_period_and_space_separator': return entier ? group(0, ' ', '.') : group(2, ' ', '.');
      default: return entier ? group(0, ' ', ',') : group(2, ' ', ',');
    }
  });
}

function gallery(card) {
  const gal = card.querySelector('[data-mv-gal]');
  const track = gal && gal.querySelector('[data-mv-track]');
  if (!track) return;
  const slides = Array.from(track.children);
  if (slides.length < 2) return;

  const dots = Array.from((gal.querySelector('[data-mv-dots]') || document.createDocumentFragment()).querySelectorAll('button'));
  const prev = gal.querySelector('[data-mv-prev]');
  const next = gal.querySelector('[data-mv-next]');

  const current = () => Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
  const goTo = (i) => {
    const k = Math.max(0, Math.min(slides.length - 1, i));
    track.scrollTo({ left: k * track.clientWidth, behavior: reduced.matches ? 'auto' : 'smooth' });
  };
  const paint = () => {
    const k = current();
    dots.forEach((d, i) => {
      if (i === k) d.setAttribute('aria-current', 'true');
      else d.removeAttribute('aria-current');
    });
  };

  if (prev) prev.addEventListener('click', () => goTo(current() - 1));
  if (next) next.addEventListener('click', () => goTo(current() + 1));
  dots.forEach((d, i) => d.addEventListener('click', () => goTo(i)));

  let raf = null;
  track.addEventListener('scroll', () => {
    if (raf) return;
    raf = requestAnimationFrame(() => { raf = null; paint(); });
  }, { passive: true });
  paint();
}

/* 6. Visionneuse --------------------------------------------------------
   Une seule pour la page, créée au premier clic et posée dans <body> (hors
   des sections, dont `overflow: hidden` couperait la position fixe). Elle
   reçoit les images de la carte cliquée : grand format (`data-mv-full`),
   flèches, clavier (Échap, ←, →), balayage sur mobile, focus gardé dans la
   fenêtre et rendu à l'élément d'origine à la fermeture. En refermant, la
   carte se cale sur la dernière image vue. */

const ICON_CLOSE = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="m3 3 10 10M13 3 3 13"/></svg>';
const ICON_PREV = '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 1 3 7l6 6"/></svg>';
const ICON_NEXT = '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 1 6 6-6 6"/></svg>';

let viewer = null;

function getViewer() {
  if (viewer) return viewer;
  const el = document.createElement('div');
  el.className = 'mv-lb';
  el.hidden = true;
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-modal', 'true');
  el.innerHTML =
    `<button type="button" class="mv-lb__close" data-mv-lb-close>${ICON_CLOSE}</button>` +
    '<div class="mv-lb__stage">' +
    '<img class="mv-lb__img" alt="" width="1200" height="1600" decoding="async">' +
    `<button type="button" class="mv-lb__btn mv-lb__btn--prev" data-mv-lb-prev>${ICON_PREV}</button>` +
    `<button type="button" class="mv-lb__btn mv-lb__btn--next" data-mv-lb-next>${ICON_NEXT}</button>` +
    '</div>' +
    '<p class="mv-lb__count" aria-live="polite"></p>';
  document.body.appendChild(el);

  const img = el.querySelector('.mv-lb__img');
  const count = el.querySelector('.mv-lb__count');
  const btnClose = el.querySelector('[data-mv-lb-close]');
  const btnPrev = el.querySelector('[data-mv-lb-prev]');
  const btnNext = el.querySelector('[data-mv-lb-next]');
  const state = { shots: [], at: 0, opener: null, track: null };

  const paint = () => {
    const shot = state.shots[state.at];
    if (!shot) return;
    img.src = shot.src;
    img.alt = shot.alt;
    const solo = state.shots.length < 2;
    btnPrev.hidden = solo;
    btnNext.hidden = solo;
    count.textContent = solo ? '' : `${state.at + 1} / ${state.shots.length}`;
  };
  const move = (step) => {
    if (state.shots.length < 2) return;
    state.at = (state.at + step + state.shots.length) % state.shots.length;
    paint();
  };
  const onKey = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); close(); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); move(-1); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); move(1); }
    else if (e.key === 'Tab') {
      const focusables = [btnClose, btnPrev, btnNext].filter((b) => !b.hidden);
      const i = focusables.indexOf(document.activeElement);
      e.preventDefault();
      const next = e.shiftKey ? (i <= 0 ? focusables.length - 1 : i - 1) : (i + 1) % focusables.length;
      focusables[next].focus();
    }
  };
  function close() {
    el.hidden = true;
    document.documentElement.style.overflow = '';
    document.removeEventListener('keydown', onKey, true);
    if (state.track) {
      state.track.scrollTo({ left: state.at * state.track.clientWidth, behavior: 'auto' });
    }
    if (state.opener && state.opener.focus) state.opener.focus({ preventScroll: true });
  }
  const open = ({ shots, at, opener, track, labels }) => {
    state.shots = shots;
    state.at = at;
    state.opener = opener;
    state.track = track;
    el.setAttribute('aria-label', labels.dialog || '');
    btnClose.setAttribute('aria-label', labels.close || '');
    btnPrev.setAttribute('aria-label', labels.prev || '');
    btnNext.setAttribute('aria-label', labels.next || '');
    paint();
    el.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey, true);
    btnClose.focus({ preventScroll: true });
  };

  btnClose.addEventListener('click', close);
  btnPrev.addEventListener('click', () => move(-1));
  btnNext.addEventListener('click', () => move(1));
  /* Clic sur le fond (hors image et boutons) : fermeture. */
  el.addEventListener('click', (e) => {
    if (!e.target.closest('.mv-lb__img, .mv-lb__btn, .mv-lb__close')) close();
  });
  let x0 = null;
  el.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
  el.addEventListener('touchend', (e) => {
    if (x0 === null) return;
    const dx = e.changedTouches[0].clientX - x0;
    if (Math.abs(dx) > 45) move(dx < 0 ? 1 : -1);
    x0 = null;
  }, { passive: true });

  viewer = { open };
  return viewer;
}

function zoom(card) {
  const gal = card.querySelector('[data-mv-gal]');
  const track = gal && gal.querySelector('[data-mv-track]');
  if (!track) return;
  const items = Array.from(track.children);
  if (!items.length) return;

  const labels = {
    dialog: gal.dataset.mvLbLabel,
    close: gal.dataset.mvLbClose,
    prev: gal.dataset.mvLbPrev,
    next: gal.dataset.mvLbNext,
  };
  const shots = () => items.map((li) => {
    const i = li.querySelector('img');
    return { src: li.dataset.mvFull || (i && (i.currentSrc || i.src)) || '', alt: (i && i.alt) || '' };
  });
  const openAt = (at, opener) => getViewer().open({ shots: shots(), at, opener, track, labels });

  const btn = gal.querySelector('[data-mv-zoom]');
  /* Une image n'est pas focusable : à la fermeture, le focus revient sur le
     bouton d'agrandissement de la carte, pas en haut de la page. */
  track.addEventListener('click', (e) => {
    const li = e.target.closest('li');
    if (!li || !track.contains(li)) return;
    openAt(items.indexOf(li), btn || card);
  });
  if (btn) {
    btn.addEventListener('click', () => {
      const at = Math.max(0, Math.min(items.length - 1, Math.round(track.scrollLeft / Math.max(track.clientWidth, 1))));
      openAt(at, btn);
    });
  }
}

/* Après un ajout réussi, un seul propriétaire du tiroir :
   - UpCart présent (le tiroir du site) : on remplace seulement la bulle du
     panier dans le header, puis on rafraîchit et on ouvre UpCart. On n'appelle
     PAS cart-notification.renderContents, qui cliquerait le résumé du tiroir
     Dawn et ouvrirait un second tiroir.
   - Sinon : chemin du thème, cart-notification.renderContents (qui ouvre le
     tiroir Dawn), comme product-form.js.
   Aucune de ces étapes ne peut faire croire à un échec : l'article est déjà
   dans le panier. Retourne vrai si une interface panier s'est ouverte. */
function upcartPresent() {
  return typeof window.upcartOpenCart === 'function' || typeof window.upcartRefreshCart === 'function';
}

/* Garde-fou limité à cette page : le thème charge dans le header un écouteur
   de l'événement UpCart `aftersell-upcart:public-events:added-to-cart` qui
   appelle `cart-notification.renderContents`, lequel clique le résumé du
   tiroir Dawn et l'ouvre en plus d'UpCart. Ici, quand UpCart tient le tiroir,
   on remplace `renderContents` sur l'instance de la page par la même mise à
   jour des fragments (bulle, contenu du tiroir) sans l'ouverture. Le fichier
   du thème n'est pas modifié : les autres pages gardent leur comportement. */
function guardNotification() {
  const notif = document.querySelector('cart-notification');
  if (!notif || notif.dataset.mvGuard) return;
  if (typeof notif.renderContents !== 'function') {
    /* Élément pas encore défini (ordre de chargement) : on réessaie à sa définition. */
    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('cart-notification').then(() => guardNotification());
    }
    return;
  }
  notif.dataset.mvGuard = '1';
  const original = notif.renderContents.bind(notif);
  /* L'enveloppe est posée quel que soit l'état d'UpCart : la propriété du
     tiroir se décide à chaque appel, pas à l'installation. */
  notif.renderContents = function (parsedState) {
    if (!upcartPresent()) return original(parsedState);
    try {
      if (!parsedState || !parsedState.sections) return undefined;
      this.cartItemKey = parsedState.key;
      this.getSectionsToRender().forEach((section) => {
        const host = document.getElementById(section.id);
        const html = parsedState.sections[section.id];
        if (!host || !html) return;
        const target = (section.selector && host.querySelector(section.selector)) || host;
        target.innerHTML = this.getSectionInnerHTML(html, section.selector);
      });
      if (this.header && typeof this.header.reveal === 'function') this.header.reveal();
    } catch (e) { /* rien : l'article est déjà dans le panier */ }
    return undefined;
  };
}

function fragmentSection(html, selector) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const el = selector ? doc.querySelector(selector) : null;
  return (el || doc.querySelector('.shopify-section') || doc.body).innerHTML;
}

function afterAdd(item, notif) {
  try {
    document.dispatchEvent(new CustomEvent('productAdded', { detail: { item } }));
  } catch (e) { /* rien */ }

  let ouvert = false;
  if (upcartPresent()) {
    try {
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && item && item.sections && item.sections['cart-icon-bubble']) {
        bubble.innerHTML = fragmentSection(item.sections['cart-icon-bubble']);
      }
    } catch (e) { /* rien */ }
    try {
      if (typeof window.upcartRefreshCart === 'function') window.upcartRefreshCart();
      if (typeof window.upcartOpenCart === 'function') { window.upcartOpenCart(); ouvert = true; }
    } catch (e) { /* rien */ }
  } else {
    try {
      if (notif && typeof notif.renderContents === 'function' && item && item.sections) {
        notif.renderContents(item);
        ouvert = true;
      }
    } catch (e) { /* rien */ }
  }
  try {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  } catch (e) { /* rien */ }
  return ouvert;
}

function cardForm(card) {
  const form = card.querySelector('[data-mv-form]');
  const addBtn = card.querySelector('[data-mv-add]');
  if (!form || !addBtn) return;

  const select = card.querySelector('[data-mv-variant]');
  const label = card.querySelector('[data-mv-lbl]');
  const priceEl = card.querySelector('[data-mv-price]');
  const wasEl = card.querySelector('[data-mv-was]');
  const msg = card.querySelector('[data-mv-msg]');
  const cartLink = card.querySelector('[data-mv-cart]');

  let data = {};
  const json = card.querySelector('[data-mv-json]');
  if (json) { try { data = JSON.parse(json.textContent); } catch (e) { data = {}; } }
  const variants = data.variants || [];

  /* La variante est celle du contrôle `id` du formulaire (le <select>, ou
     l'input caché d'un produit sans option) : même source pour l'envoi natif
     et pour l'AJAX. Le prix rendu par Liquid fait foi tant que la taille n'a
     pas changé. */
  const variantId = () => parseInt((form.elements.id && form.elements.id.value) || '', 10);
  const paintLabel = () => {
    const v = variants.find((x) => x.id === variantId());
    if (!v) return;
    addBtn.disabled = !v.available;
    if (label) label.textContent = v.available ? (data.labelAdd || 'Ajouter au panier') : (data.labelOut || 'Bientôt disponible');
  };
  const sync = (paintPrice) => {
    const v = variants.find((x) => x.id === variantId());
    if (!v) return;
    if (paintPrice && priceEl) {
      priceEl.textContent = money(v.price, data.moneyFormat);
      if (wasEl) {
        if (v.compare && v.compare > v.price) { wasEl.textContent = money(v.compare, data.moneyFormat); wasEl.hidden = false; }
        else wasEl.hidden = true;
      }
    }
    paintLabel();
    if (msg) msg.hidden = true;
  };
  /* Changer de taille est la seule interaction qui retire le lien « Voir le panier ». */
  if (select) select.addEventListener('change', () => { sync(true); if (cartLink) cartLink.hidden = true; });
  sync(false);

  form.addEventListener('submit', (e) => {
    if (!window.fetch) return; /* très vieux navigateur : envoi natif */
    e.preventDefault();
    if (addBtn.disabled || addBtn.classList.contains('is-busy')) return;

    addBtn.classList.add('is-busy');
    if (msg) msg.hidden = true;

    const notif = document.querySelector('cart-notification');
    const body = new FormData(form);
    /* Fragments demandés à Shopify avec l'ajout : la bulle seule si UpCart
       tient le tiroir, les sections du tiroir Dawn sinon. */
    if (upcartPresent()) {
      body.append('sections', 'cart-icon-bubble');
      body.append('sections_url', window.location.pathname);
    } else if (notif && typeof notif.getSectionsToRender === 'function') {
      body.append('sections', notif.getSectionsToRender().map((s) => s.id).join(','));
      body.append('sections_url', window.location.pathname);
    }
    const url = (window.routes && window.routes.cart_add_url) || form.getAttribute('action') || '/cart/add';

    fetch(url, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body,
    })
      .then((r) => r.json())
      .then((res) => {
        if (res && res.status) {
          /* Refus de Shopify (rupture, variante inconnue) : l'article n'est pas ajouté. */
          if (msg) { msg.textContent = res.description || res.message || data.labelError || ''; msg.hidden = false; }
          return;
        }
        const ouvert = afterAdd(res, notif);
        if (label) label.textContent = data.labelAdded || 'Ajouté';
        if (!ouvert && cartLink) cartLink.hidden = false; /* reste visible : c'est le seul chemin vers le panier */
        setTimeout(paintLabel, 2200); /* seul le libellé du bouton redevient « Ajouter » */
      })
      .catch(() => {
        /* Réseau ou réponse illisible : rien n'a été ajouté de façon certaine. */
        if (msg) { msg.textContent = data.labelError || ''; msg.hidden = false; }
      })
      .then(() => addBtn.classList.remove('is-busy'));
  });
}

function cards(root) {
  root.querySelectorAll('[data-mv-card]').forEach((card) => {
    if (card.dataset.mvReady) return;
    card.dataset.mvReady = '1';
    gallery(card);
    zoom(card);
    /* Les cartes du composeur ajoutent à la sélection, pas au panier (8). */
    if (!card.hasAttribute('data-mv-compose')) cardForm(card);
  });
}

/* 7. Guide des tailles ---------------------------------------------------
   Les modales sont celles des fiches produit (snippet size-guide-html),
   rendues par chaque section et déplacées dans <body> par ModalDialog
   (global.js). Le script du tableau (défilement, conseils repliés sur mobile)
   n'est chargé qu'une fois, et seulement si la page a une modale. */

function guides(root) {
  root.querySelectorAll('[data-mv-guide]').forEach((btn) => {
    if (btn.dataset.mvReady) return;
    btn.dataset.mvReady = '1';
    btn.addEventListener('click', () => {
      const modal = document.getElementById(btn.dataset.mvGuide);
      if (!modal) return;
      if (typeof modal.show === 'function') modal.show(btn);
      else modal.setAttribute('open', '');
    });
  });
  const flag = document.querySelector('[data-mv-sgh]');
  if (flag && flag.dataset.mvSgh && !document.querySelector('script[src*="size-guide-html"]')) {
    const tag = document.createElement('script');
    tag.src = flag.dataset.mvSgh;
    tag.defer = true;
    document.body.appendChild(tag);
  }
}

/* 8. Composeur -----------------------------------------------------------
   La cliente ajoute des culottes (taille choisie sur la carte) dans des
   emplacements, en change la taille ou les retire ; la section affiche la
   remise, le total et la jauge des paliers, puis envoie toute la sélection au
   panier en un appel.

   Remise affichée : la règle de Shopify sans abonnement Plus, une seule remise
   produit par article, la meilleure. Pour chaque culotte, on retient donc la
   plus forte entre l'offre de lancement (si elle compte pour l'offre, que les
   dates sont en cours et que la sélection atteint son minimum) et le palier
   atteint (si elle compte pour les paliers). Rien n'est appliqué ici : au
   panier, ce sont les remises automatiques de l'admin qui font foi. */

const ICON_PLUS = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>';
const ICON_CARET = '<svg class="mv-select__caret" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m2.5 4.5 3.5 3.5 3.5-3.5"/></svg>';
const NBSP = '\u00a0';

function esc(text) {
  return String(text == null ? '' : text).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

/* [nombre] et [remise] dans les textes de l'éditeur. */
function fill(text, n, pct) {
  return String(text || '').replace(/\[nombre\]/g, String(n)).replace(/\[remise\]/g, String(pct));
}

/* « 2026-09-24 09:00 » lu à l'heure de Paris, en millisecondes. Vide ou
   illisible : null, c'est-à-dire pas de borne. */
function parisTime(text) {
  const m = /^\s*(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T]+(\d{1,2})(?:[:h](\d{2}))?)?\s*$/.exec(text || '');
  if (!m) return null;
  const y = +m[1];
  const mo = +m[2];
  const d = +m[3];
  const h = +(m[4] || 0);
  const mi = +(m[5] || 0);
  const guess = Date.UTC(y, mo - 1, d, h, mi);
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Paris', hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    }).formatToParts(new Date(guess));
    const get = (type) => +parts.find((p) => p.type === type).value;
    const shown = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'));
    return guess - (shown - guess);
  } catch (e) {
    return guess - 2 * 3600 * 1000; /* repli : heure d'été de Paris */
  }
}

function offerLive(offer, now) {
  if (!offer || !offer.on || !(offer.pct > 0) || !(offer.min > 0)) return false;
  const t = now === undefined ? Date.now() : now;
  const a = parisTime(offer.start);
  const b = parisTime(offer.end);
  if (a !== null && t < a) return false;
  if (b !== null && t >= b) return false;
  return true;
}

/* Prix de la sélection. items : [{ price, offer, pack }] ; prix en centimes. */
function priceSelection(items, tiers, offer, live) {
  const offerCount = items.filter((it) => it.offer).length;
  const packCount = items.filter((it) => it.pack).length;
  const offerPct = live && offerCount >= offer.min ? offer.pct : 0;
  let packPct = 0;
  tiers.forEach((t) => { if (packCount >= t.min && t.pct > packPct) packPct = t.pct; });
  let subtotal = 0;
  let saving = 0;
  let pct = 0;
  items.forEach((it) => {
    const p = Math.max(it.offer ? offerPct : 0, it.pack ? packPct : 0);
    subtotal += it.price;
    saving += Math.round((it.price * p) / 100);
    if (p > pct) pct = p;
  });
  return { subtotal, saving, total: subtotal - saving, pct };
}

/* Paliers qui améliorent vraiment la remise, dans l'ordre : avec l'offre à
   2 culottes (-15 %), le palier 3 culottes (-10 %) ne change rien et n'est pas
   affiché. */
function improvingSteps(tiers, offer, live) {
  const list = tiers.slice();
  if (live) list.push({ min: offer.min, pct: offer.pct });
  list.sort((a, b) => a.min - b.min || b.pct - a.pct);
  const out = [];
  list.forEach((t) => { if (!out.length || t.pct > out[out.length - 1].pct) out.push(t); });
  return out;
}

/* Combien de culottes en plus (comptant pour l'offre et les paliers) pour une
   remise supérieure ; null si aucune, ou au-delà du maximum. */
function nextStep(items, tiers, offer, live, current, max) {
  const probe = { price: 0, offer: true, pack: true };
  for (let k = 1; items.length + k <= max; k++) {
    const r = priceSelection(items.concat(Array(k).fill(probe)), tiers, offer, live);
    if (r.pct > current.pct) return { n: k, pct: r.pct };
  }
  return null;
}

function composer(root) {
  root.querySelectorAll('[data-mv-cz]').forEach((cz) => {
    if (cz.dataset.mvReady) return;
    let cfg = null;
    try { cfg = JSON.parse(cz.querySelector('[data-mv-cz-json]').textContent); } catch (e) { cfg = null; }
    if (!cfg) return;
    cz.dataset.mvReady = '1';

    const L = cfg.labels || {};
    const fmt = cfg.moneyFormat;
    const max = Math.max(1, +cfg.max || 12);
    const slotsMin = Math.max(1, +cfg.slotsMin || 2);
    const offer = { on: !!(cfg.offer && cfg.offer.on), min: +(cfg.offer && cfg.offer.min) || 0, pct: +(cfg.offer && cfg.offer.pct) || 0, start: cfg.offer && cfg.offer.start, end: cfg.offer && cfg.offer.end };
    const tiers = (cfg.tiers || []).map((t) => ({ min: +t.min || 0, pct: +t.pct || 0 })).filter((t) => t.min > 0 && t.pct > 0);
    const q = (sel) => cz.querySelector(sel);
    const els = {
      slots: q('[data-mv-cz-slots]'),
      badge: q('[data-mv-cz-badge]'),
      status: q('[data-mv-cz-status]'),
      total: q('[data-mv-cz-total]'),
      totalRow: q('[data-mv-cz-totalrow]'),
      was: q('[data-mv-cz-was]'),
      submits: Array.from(cz.querySelectorAll('[data-mv-cz-submit]')),
      msg: q('[data-mv-cz-msg]'),
      gauge: q('[data-mv-cz-gauge]'),
      hint: q('[data-mv-cz-hint]'),
      meter: q('[data-mv-cz-meter]'),
      fill: q('[data-mv-cz-fill]'),
      grid: q('[data-mv-cz-grid]'),
      pickhead: q('.mv-cz__pickhead'),
      bar: q('[data-mv-cz-bar]'),
      barMsg: q('[data-mv-cz-barmsg]'),
      barCount: q('[data-mv-cz-barcount]'),
      barTotal: q('[data-mv-cz-bartotal]'),
      barWas: q('[data-mv-cz-barwas]'),
      live: q('[data-mv-cz-live]'),
      gift: q('[data-mv-cz-gift]'),
      giftText: q('[data-mv-cz-gifttext]'),
    };
    if (!els.slots) return;

    const narrow = window.matchMedia('(max-width: 989px)');
    let items = [];
    let seq = 0;
    let busy = false;
    let inView = false;
    let live = offerLive(offer);
    const cards = new Map();

    /* Cadeau MOVE : seuil sur le panier entier. Le montant restant compte la
       sélection et ce que contient déjà le panier (lu dans /cart.js). */
    const gift = cfg.gift && +cfg.gift.min > 0 ? cfg.gift : null;
    let cartTotal = 0;
    const giftHTML = (text, amount) => esc(text || '')
      .replace('[montant]', esc(money(amount, fmt)).replace(/ (?=€)/g, NBSP))
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    const loadCart = () => {
      if (!gift || !window.fetch) return;
      const url = `${((window.routes && window.routes.cart_url) || '/cart').replace(/\/$/, '')}.js`;
      fetch(url, { headers: { Accept: 'application/json' } })
        .then((res) => res.json())
        .then((c) => { cartTotal = Math.max(0, +c.total_price || 0); paintSummary(); })
        .catch(() => { /* sans panier, la sélection seule compte */ });
    };

    const announce = (text) => {
      if (!els.live) return;
      els.live.textContent = '';
      setTimeout(() => { els.live.textContent = text; }, 60);
    };
    const pctLabel = (pct) => `-${pct}${NBSP}%`;
    const countLabel = (n, pct) => fill(n === 1 ? L.countOne : L.countMany, n, pct);

    /* Textes « pendant l'offre » / « hors offre » selon les dates. */
    const paintOffer = () => {
      cz.querySelectorAll('[data-mv-cz-offer="on"]').forEach((el) => { el.hidden = !live; });
      cz.querySelectorAll('[data-mv-cz-offer="off"]').forEach((el) => { el.hidden = live; });
    };

    /* ---- messages ---- */
    const showMsg = (text, error, withCart) => {
      [els.msg, els.barMsg].forEach((el) => {
        if (!el) return;
        el.textContent = text || '';
        if (withCart && cfg.cartUrl) {
          const a = document.createElement('a');
          a.href = cfg.cartUrl;
          a.textContent = L.cart || '';
          el.append(' ', a);
        }
        el.classList.toggle('is-error', !!error);
        el.hidden = !text;
      });
    };
    const clearMsg = () => showMsg('', false, false);

    /* ---- emplacements ---- */
    const slotHTML = (it) => {
      const entry = cards.get(it.key);
      const d = entry ? entry.data : { name: '', variants: [] };
      const vars = d.variants || [];
      const sizeId = `${cz.id || 'mv-cz'}-size-${it.uid}`;
      const options = vars.map((v) => `<option value="${v.id}"${v.id === it.id ? ' selected' : ''}${v.available ? '' : ' disabled'}>${esc(v.title)}${v.available ? '' : ` · ${esc(L.out)}`}</option>`).join('');
      return `<li class="mv-cz__slot" data-uid="${it.uid}">`
        + (d.thumb ? `<img class="mv-cz__thumb" src="${esc(d.thumb)}" alt="" width="240" height="320" loading="lazy">` : '<span class="mv-cz__thumb" aria-hidden="true"></span>')
        + '<div class="mv-cz__slotbody">'
        + (d.tag ? `<span class="mv-card__tag mv-card__tag--${esc(d.gamme)}">${esc(d.tag)}</span>` : '')
        + `<p class="mv-cz__slotname">${esc(d.name)}</p>`
        + `<p class="mv-cz__slotprice" data-mv-cz-price>${esc(money(it.price, fmt))}</p>`
        + (vars.length > 1
          ? `<div class="mv-select mv-cz__size"><label class="mv-vh" for="${sizeId}">${esc(L.size)} · ${esc(d.name)}</label><select class="mv-select__input" id="${sizeId}" data-mv-cz-size>${options}</select>${ICON_CARET}</div>`
          : '')
        + '</div>'
        + `<button type="button" class="mv-cz__remove" data-mv-cz-remove aria-label="${esc(L.remove)} : ${esc(d.name)}">${ICON_CLOSE}</button>`
        + '</li>';
    };
    const emptyHTML = (text) => `<li class="mv-cz__slot mv-cz__slot--empty"><button type="button" class="mv-cz__add" data-mv-cz-goto><span class="mv-cz__plus" aria-hidden="true">${ICON_PLUS}</span><span>${esc(text)}</span></button></li>`;

    const paintSlots = (addedUid) => {
      const empty = items.length < slotsMin ? slotsMin - items.length : (items.length < max ? 1 : 0);
      let html = items.map(slotHTML).join('');
      for (let i = 0; i < empty; i++) html += emptyHTML(items.length < slotsMin ? L.slotEmpty : L.slotMore);
      els.slots.innerHTML = html;
      els.slots.classList.toggle('is-many', items.length + empty > 2);
      if (addedUid) {
        const li = els.slots.querySelector(`[data-uid="${addedUid}"]`);
        if (li) {
          li.classList.add('is-new');
          /* Sur mobile, le rail des emplacements se cale sur la nouvelle culotte
             sans faire défiler la page. */
          if (els.slots.scrollWidth > els.slots.clientWidth + 2) {
            const pad = parseFloat(getComputedStyle(els.slots).paddingLeft) || 0;
            els.slots.scrollTo({ left: Math.max(0, li.offsetLeft - pad), behavior: reduced.matches ? 'auto' : 'smooth' });
          }
        }
      }
    };

    /* ---- récapitulatif, jauge, cartes, barre ---- */
    const paintSummary = () => {
      const r = priceSelection(items, tiers, offer, live);
      const n = items.length;

      if (els.badge) {
        els.badge.hidden = !(r.pct > 0);
        els.badge.textContent = r.pct > 0 ? pctLabel(r.pct) : '';
      }
      if (els.status) els.status.textContent = n ? countLabel(n, r.pct) : (L.selection || '');
      if (els.total) els.total.textContent = n ? money(r.total, fmt) : (L.empty || '');
      if (els.totalRow) els.totalRow.classList.toggle('is-empty', !n);
      if (els.was) {
        els.was.hidden = !(r.saving > 0);
        els.was.textContent = r.saving > 0 ? money(r.subtotal, fmt) : '';
      }
      els.submits.forEach((b) => { b.disabled = busy || n === 0; });

      /* Jauge */
      const steps = improvingSteps(tiers, offer, live);
      if (els.gauge) {
        if (!steps.length) {
          els.gauge.hidden = true;
        } else {
          els.gauge.hidden = false;
          let text = '';
          const next = nextStep(items, tiers, offer, live, r, max);
          if (!n) text = fill(L.start, steps[0].min, steps[0].pct);
          else if (next) text = fill(next.n === 1 ? L.nextOne : L.nextMany, next.n, next.pct);
          else if (r.pct > 0) text = fill(L.best, n, r.pct);
          if (els.hint) els.hint.textContent = text;
          if (els.meter && els.fill) {
            const counted = items.filter((it) => it.pack || (live && it.offer)).length;
            const last = steps[steps.length - 1].min;
            els.fill.style.width = `${Math.min(100, (counted / last) * 100)}%`;
            els.meter.querySelectorAll('.mv-cz__step').forEach((m) => m.remove());
            steps.forEach((st) => {
              const mark = document.createElement('span');
              mark.className = `mv-cz__step${counted >= st.min ? ' is-on' : ''}`;
              mark.style.left = `${(st.min / last) * 100}%`;
              mark.innerHTML = `<span class="mv-cz__steplbl">${esc(pctLabel(st.pct))}</span>`;
              els.meter.appendChild(mark);
            });
          }
        }
      }

      /* Cadeau MOVE */
      if (gift && els.giftText) {
        let text = gift.idle;
        let amount = 0;
        let ok = false;
        if (n) {
          amount = gift.min - (r.total + cartTotal);
          ok = amount <= 0;
          text = ok ? gift.ok : gift.need;
        }
        els.giftText.innerHTML = giftHTML(text, Math.max(0, amount));
        if (els.gift) els.gift.classList.toggle('is-ok', ok);
      }

      /* Cartes : contour et compteur */
      cards.forEach((entry, key) => {
        const c = items.filter((it) => it.key === key).length;
        entry.card.classList.toggle('is-picked', c > 0);
        if (entry.count) {
          entry.count.hidden = c === 0;
          entry.count.textContent = c ? fill(L.inSelection, c, r.pct) : '';
        }
      });

      /* Barre mobile */
      cz.classList.toggle('has-items', n > 0);
      if (els.bar) {
        if (els.barCount) els.barCount.textContent = n ? countLabel(n, r.pct) + (r.pct > 0 ? ` · ${pctLabel(r.pct)}` : '') : '';
        if (els.barTotal) els.barTotal.textContent = n ? money(r.total, fmt) : '';
        if (els.barWas) {
          els.barWas.hidden = !(r.saving > 0);
          els.barWas.textContent = r.saving > 0 ? money(r.subtotal, fmt) : '';
        }
        const on = narrow.matches && inView && n > 0;
        els.bar.classList.toggle('is-on', on);
        els.bar.setAttribute('aria-hidden', on ? 'false' : 'true');
        if (on) els.bar.removeAttribute('inert');
        else els.bar.setAttribute('inert', '');
      }
    };

    const paint = (addedUid) => {
      paintSlots(addedUid);
      paintSummary();
    };

    /* ---- cartes ---- */
    cz.querySelectorAll('[data-mv-compose]').forEach((card) => {
      let data = null;
      try { data = JSON.parse(card.querySelector('[data-mv-json]').textContent); } catch (e) { data = null; }
      const form = card.querySelector('[data-mv-form]');
      if (!data || !form) return;
      const entry = {
        card,
        data,
        form,
        select: card.querySelector('[data-mv-variant]'),
        btn: card.querySelector('[data-mv-add]'),
        label: card.querySelector('[data-mv-lbl]'),
        count: card.querySelector('[data-mv-count]'),
        msg: card.querySelector('[data-mv-msg]'),
        price: card.querySelector('[data-mv-price]'),
        was: card.querySelector('[data-mv-was]'),
        timer: null,
      };
      cards.set(data.key, entry);

      const cardMsg = (text) => {
        if (!entry.msg) return;
        entry.msg.textContent = text || '';
        entry.msg.hidden = !text;
      };

      if (entry.select) {
        entry.select.addEventListener('change', () => {
          const v = (data.variants || []).find((x) => x.id === parseInt(entry.select.value, 10));
          if (v && entry.price) entry.price.textContent = money(v.price, fmt);
          if (v && entry.was) {
            entry.was.hidden = !(v.compare > v.price);
            entry.was.textContent = v.compare > v.price ? money(v.compare, fmt) : '';
          }
          card.classList.remove('is-missing');
          cardMsg('');
        });
      }

      /* Le clic sur le bouton (souris, clavier, tactile) ajoute à la sélection
         et empêche l'envoi du formulaire ; sans JavaScript, le formulaire
         ajoute au panier. */
      const addToSelection = (e) => {
        e.preventDefault();
        const field = form.elements.id;
        const id = parseInt((field && field.value) || '', 10);
        const v = id ? (data.variants || []).find((x) => x.id === id) : null;
        if (!v) {
          card.classList.add('is-missing');
          cardMsg(L.pickSize || data.labelPickSize);
          if (entry.select) entry.select.focus();
          return;
        }
        if (!v.available) return;
        if (items.length >= max) {
          cardMsg(L.max);
          return;
        }
        seq += 1;
        items.push({ uid: seq, key: data.key, id: v.id, price: v.price, offer: !!data.offer, pack: !!data.pack });
        cardMsg('');
        clearMsg();
        paint(seq);
        announce(`${data.name}, ${v.title} : ${L.chosen || ''}`);

        if (entry.label && entry.btn) {
          clearTimeout(entry.timer);
          entry.btn.classList.add('is-done');
          entry.label.textContent = L.chosen || data.labelChoose;
          entry.timer = setTimeout(() => {
            entry.btn.classList.remove('is-done');
            entry.label.textContent = data.labelChoose;
          }, 1600);
        }
      };
      if (entry.btn) entry.btn.addEventListener('click', addToSelection);
      form.addEventListener('submit', addToSelection);
    });

    /* ---- emplacements : taille, retrait, aller aux cartes ---- */
    els.slots.addEventListener('change', (e) => {
      const select = e.target.closest('[data-mv-cz-size]');
      if (!select) return;
      const li = select.closest('[data-uid]');
      const it = li && items.find((x) => String(x.uid) === li.dataset.uid);
      const entry = it && cards.get(it.key);
      const v = entry && (entry.data.variants || []).find((x) => x.id === parseInt(select.value, 10));
      if (!v) return;
      it.id = v.id;
      it.price = v.price;
      const priceEl = li.querySelector('[data-mv-cz-price]');
      if (priceEl) priceEl.textContent = money(v.price, fmt);
      clearMsg();
      paintSummary();
    });

    els.slots.addEventListener('click', (e) => {
      const remove = e.target.closest('[data-mv-cz-remove]');
      if (remove) {
        const li = remove.closest('[data-uid]');
        const index = Array.prototype.indexOf.call(els.slots.children, li);
        items = items.filter((x) => String(x.uid) !== li.dataset.uid);
        clearMsg();
        paint();
        const target = els.slots.children[Math.min(index, els.slots.children.length - 1)];
        const focusable = target && target.querySelector('[data-mv-cz-remove], [data-mv-cz-goto]');
        if (focusable) focusable.focus({ preventScroll: true });
        return;
      }
      if (e.target.closest('[data-mv-cz-goto]') && els.grid) {
        (els.pickhead || els.grid).scrollIntoView({ behavior: reduced.matches ? 'auto' : 'smooth', block: 'start' });
        const first = Array.from(els.grid.querySelectorAll('[data-mv-compose]')).find((c) => !c.hidden);
        const field = first && first.querySelector('[data-mv-variant], [data-mv-add]');
        if (field) setTimeout(() => field.focus({ preventScroll: true }), reduced.matches ? 0 : 450);
      }
    });

    /* ---- filtres ---- */
    const filters = Array.from(cz.querySelectorAll('[data-mv-cz-filter]'));
    filters.forEach((btn) => {
      btn.addEventListener('click', () => {
        const f = btn.dataset.mvCzFilter;
        filters.forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
        cards.forEach((entry) => {
          entry.card.hidden = f !== 'all' && entry.card.dataset.gamme !== f;
        });
        if (els.grid) els.grid.scrollLeft = 0;
      });
    });

    /* ---- ajout au panier ---- */
    const refreshCart = () => {
      try { if (typeof window.upcartRefreshCart === 'function') window.upcartRefreshCart(); } catch (e) { /* rien */ }
      try { document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true })); } catch (e) { /* rien */ }
    };

    const submit = () => {
      if (busy || !items.length || !window.fetch) return;
      busy = true;
      els.submits.forEach((b) => { b.disabled = true; b.classList.add('is-busy'); });
      clearMsg();

      const counts = new Map();
      items.forEach((it) => counts.set(it.id, (counts.get(it.id) || 0) + 1));
      const body = { items: Array.from(counts, ([id, quantity]) => ({ id, quantity })) };
      const notif = document.querySelector('cart-notification');
      if (upcartPresent()) {
        body.sections = 'cart-icon-bubble';
        body.sections_url = window.location.pathname;
      } else if (notif && typeof notif.getSectionsToRender === 'function') {
        body.sections = notif.getSectionsToRender().map((sec) => sec.id).join(',');
        body.sections_url = window.location.pathname;
      }
      const base = ((window.routes && window.routes.cart_add_url) || '/cart/add').replace(/\.js$/, '');

      fetch(`${base}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
      })
        .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
        .then(({ ok, data }) => {
          if (!ok || !data || data.status) {
            /* Refus de Shopify (stock…) : une partie a pu entrer, on
               rafraîchit le panier et on garde la sélection. */
            refreshCart();
            showMsg((data && (data.description || data.message)) || L.error, true, false);
            announce((data && data.description) || L.error || '');
            return;
          }
          const lines = data.items || [];
          const last = lines.length ? lines[lines.length - 1] : {};
          const opened = afterAdd(Object.assign({}, last, { sections: data.sections }), notif);
          items = [];
          busy = false;
          paint();
          loadCart();
          showMsg(L.success, false, !opened);
          announce(L.success || '');
        })
        .catch(() => {
          showMsg(L.error, true, false);
        })
        .then(() => {
          busy = false;
          els.submits.forEach((b) => b.classList.remove('is-busy'));
          paintSummary();
        });
    };
    els.submits.forEach((b) => b.addEventListener('click', submit));

    /* ---- barre mobile : visible quand la section est à l'écran ---- */
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        entries.forEach((entry) => { inView = entry.isIntersecting; });
        paintSummary();
      }, { rootMargin: '0px 0px -25% 0px' }).observe(cz);
    }
    const onNarrow = () => paintSummary();
    if (narrow.addEventListener) narrow.addEventListener('change', onNarrow);
    else if (narrow.addListener) narrow.addListener(onNarrow);

    /* ---- début et fin de l'offre sans recharger la page ---- */
    if (offer.on && (parisTime(offer.start) !== null || parisTime(offer.end) !== null)) {
      const timer = setInterval(() => {
        if (!cz.isConnected) { clearInterval(timer); return; }
        const now = offerLive(offer);
        if (now === live) return;
        live = now;
        paintOffer();
        paintSummary();
      }, 30000);
    }

    paintOffer();
    paint();
    loadCart();
  });
}

/* 9. Offre de lancement ---------------------------------------------------
   Pastilles et liens rendus par Liquid quand l'offre est cochée dans les
   réglages du thème, masqués par `hidden` si elle n'est pas en cours. Ici, le
   calcul est refait dans le navigateur (heure de Paris) : il corrige une page
   servie depuis le cache après l'heure de fin et masque les pastilles pendant
   la visite quand l'heure tombe. Un lien vers le composeur disparaît si la
   section n'est pas sur la page. */

let offersTimer = null;

function paintOffers() {
  const now = Date.now();
  const hasComposer = !!document.getElementById('move-composeur');
  document.querySelectorAll('[data-mv-offer]').forEach((el) => {
    const live = offerLive({ on: true, pct: 1, min: 1, start: el.dataset.mvOfferStart, end: el.dataset.mvOfferEnd }, now);
    el.hidden = !live || (el.hasAttribute('data-mv-offer-link') && !hasComposer);
  });
}

function offers() {
  const els = document.querySelectorAll('[data-mv-offer]');
  if (!els.length) return;
  paintOffers();
  const dated = Array.from(els).some((el) => parisTime(el.dataset.mvOfferStart) !== null || parisTime(el.dataset.mvOfferEnd) !== null);
  if (dated && !offersTimer) offersTimer = setInterval(paintOffers, 30000);
}

/* ------------------------------------------------------------------------ */

function init(root) {
  reveal(root);
  techno(root);
  techLines(root);
  trustpilot(root);
  cards(root);
  guides(root);
  composer(root);
  offers();
  /* À chaque initialisation, y compris au rechargement du header dans
     l'éditeur (nouvelle instance de cart-notification, sans data-mv-guard). */
  guardNotification();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init(document));
} else {
  init(document);
}

document.addEventListener('shopify:section:load', (e) => init(e.target || document));

/* Éditeur : une section retirée ou rechargée laisse ses modales de guide dans
   <body> (ModalDialog les y a déplacées) ; on les retire avec elle. */
document.addEventListener('shopify:section:unload', (e) => {
  const host = e.target;
  if (!host || !host.querySelectorAll) return;
  host.querySelectorAll('[data-mv-guide]').forEach((btn) => {
    const modal = document.getElementById(btn.dataset.mvGuide);
    if (modal && !host.contains(modal)) modal.remove();
  });
});
